// Shared atoms for admin read-only detail viewers.
// Kept minimal — deliberately mimics a document layout, not an app form.
import { Link } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";

export const inr = (v) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(v || 0));

export const inrDec = (v) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(v || 0));

export const fmtDate = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return String(v); }
};

export const fmtDateTime = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(v); }
};

// Safe render — never lets an object through to JSX (audit-log crash guard pattern).
export const safe = (v) => {
  if (v == null || v === "") return "—";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  try { return JSON.stringify(v); } catch { return "—"; }
};

// Coerce any value to an array we can safely iterate/map over.
// Fixes crashes when legacy documents store a scalar (e.g. `complaints: "text"`)
// where the current schema expects an array.
export const arr = (v) => {
  if (Array.isArray(v)) return v;
  if (v == null || v === "") return [];
  return [v];
};

export const ReadOnlyBanner = () => (
  <div
    className="rounded-md bg-[#F1FAEE] border border-[#12B76A]/25 text-[#027A48] px-4 py-2.5 flex items-center gap-2 text-[13px] font-semibold"
    data-testid="read-only-banner"
  >
    <Lock size={14} /> Workshop Record — Read Only
  </div>
);

export const HighValueBadge = ({ show }) => {
  if (!show) return null;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider bg-[#e63946]/8 text-[#e63946] border-[#e63946]/30"
      data-testid="high-value-badge"
    >
      High Value
    </span>
  );
};

export const DetailHeader = ({ crumb, backTo, title, subtitle, right }) => (
  <div className="space-y-2">
    <Link to={backTo} className="inline-flex items-center gap-1 text-[12px] text-[#457b9d] hover:underline" data-testid="detail-back-link">
      <ArrowLeft size={13} /> {crumb}
    </Link>
    <div className="flex items-end justify-between gap-4 flex-wrap">
      <div>
        <h1 className="text-[26px] md:text-[28px] font-bold text-[#1d3557] font-mono flex items-center gap-3">
          {title}
        </h1>
        {subtitle && <div className="text-[13px] text-[#667085] mt-0.5">{subtitle}</div>}
      </div>
      {right && <div className="flex items-center gap-2">{right}</div>}
    </div>
  </div>
);

export const Section = ({ title, children, testId }) => (
  <section className="bg-white rounded-xl border border-[#E4E7EC]" data-testid={testId}>
    {title && (
      <div className="px-5 py-3 border-b border-[#F1FAEE] text-[11px] uppercase tracking-[0.14em] font-bold text-[#1d3557]">
        {title}
      </div>
    )}
    <div className="p-5">{children}</div>
  </section>
);

// A responsive definition list. Pairs is [[label, value], ...].
export const DL = ({ pairs, cols = 3 }) => (
  <dl className={`grid grid-cols-1 md:grid-cols-${cols === 4 ? "4" : cols === 2 ? "2" : "3"} gap-x-8 gap-y-3`}>
    {pairs.map(([k, v], i) => (
      <div key={i}>
        <dt className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">{k}</dt>
        <dd className="text-[13px] text-[#1d3557] font-medium mt-0.5 break-words">{safe(v)}</dd>
      </div>
    ))}
  </dl>
);
